import { Injectable } from '@angular/core';

export interface PickerOverlayConfig {
  title: string;
  instructions: string[];
  showEscapeHint?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class PickerOverlayService {
  private activeOverlay: HTMLElement | null = null;
  private activeEscapeHandler: ((evt: KeyboardEvent) => void) | null = null;

  /**
   * Shows the picker overlay with the specified configuration
   *
   * @param config Configuration for the overlay text and behavior
   * @param onEscape Callback function to execute when escape key is pressed
   * @returns A function that can be called to hide the overlay
   */
  show(config: PickerOverlayConfig, onEscape?: () => void): () => void {
    // Don't show multiple overlays
    if (this.activeOverlay) {
      this.hide();
    }

    // Find the target container (same logic as the robot graph)
    const targetContainer = this.findTargetContainer();
    if (!targetContainer) {
      console.warn('Could not find target container for picker overlay');
      return () => {
        // No-op cleanup function when overlay creation fails
      };
    }

    // Create the overlay element
    this.activeOverlay = this.createOverlayElement(config);
    targetContainer.appendChild(this.activeOverlay);

    // Set up escape key handler
    if (onEscape) {
      this.activeEscapeHandler = (evt: KeyboardEvent) => {
        if (evt.key === 'Escape') {
          onEscape();
          this.hide();
        }
      };
      document.addEventListener('keydown', this.activeEscapeHandler);
    }

    // Return cleanup function
    return () => this.hide();
  }

  /**
   * Hides the currently displayed overlay
   */
  hide(): void {
    if (this.activeOverlay) {
      this.activeOverlay.remove();
      this.activeOverlay = null;
    }

    if (this.activeEscapeHandler) {
      document.removeEventListener('keydown', this.activeEscapeHandler);
      this.activeEscapeHandler = null;
    }
  }

  private findTargetContainer(): Element | null {
    // Find the widgets-layer container (same logic as robot graph)
    const widgetsLayer = document.body.querySelector('.widgets-layer');
    if (!widgetsLayer) {
      console.warn('Could not find widgets-layer container for picker overlay');
      return null;
    }

    // Find the first as-split-area within the widgets layer
    const targetContainer = widgetsLayer.querySelector('as-split-area');
    if (!targetContainer) {
      console.warn('Could not find as-split-area container within widgets-layer for picker overlay');
      return null;
    }

    return targetContainer;
  }

  private createOverlayElement(config: PickerOverlayConfig): HTMLElement {
    // Create the overlay element
    const overlay = document.createElement('div');
    overlay.className = 'picker-overlay-service';
    overlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: 3px solid rgba(32, 230, 148, 0.5);
      pointer-events: none;
      z-index: 1000;
    `;

    // Create the label element
    const label = document.createElement('div');
    label.className = 'label';
    label.style.cssText = `
      position: absolute;
      color: white;
      right: 0;
      top: 0;
      background-color: rgba(32, 230, 148, 1);
      padding: 6px 12px;
      border-bottom-left-radius: 3px;
      font-size: 12px;
      line-height: 20px;
    `;

    // Build instructions HTML
    const instructionsHtml = config.instructions.map((instruction) => `<div>${instruction}</div>`).join('');

    const escapeHintHtml = config.showEscapeHint !== false ? '<div><em>`esc` key</em> to cancel selection</div>' : '';

    // Add label content
    label.innerHTML = `
      <span>${config.title}</span>
      <div style="font-size: 10px; margin-top: 4px;">
        ${instructionsHtml}
        ${escapeHintHtml}
      </div>
    `;

    overlay.appendChild(label);
    return overlay;
  }
}
